import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Lock } from "lucide-react";
import { cx, formatBytes, formatDateTime } from "@amber/ui";
import type { EvidenceRecord, TurnView } from "../../contracts/index";
import { checksFact, languageName } from "../format";

type DocumentData = NonNullable<EvidenceRecord["document"]>;
type Block = DocumentData["blocks"][number];
interface DocumentPage {
  key: string;
  blocks: Block[];
}

function pageOf(block: Block): string {
  if (block.column) return `Col ${block.column}`;
  if (block.page) return `Page ${String(block.page)}`;
  return "";
}

function longDate(value: string | undefined): string {
  if (!value) return "";
  const d = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  return Number.isNaN(d.getTime())
    ? value
    : new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" }).format(d);
}

export function paragraphLabel(turn: TurnView): string | null {
  const l = turn.locator;
  if (!l) return null;
  const [from, to] = l.paragraphs;
  return from === to ? `¶${String(from)}` : `¶${String(from)}–¶${String(to)}`;
}

export function locatorDetail(turn: TurnView): string | null {
  const l = turn.locator;
  if (!l) return null;
  if (l.column) return `col ${l.column}`;
  if (l.page) return `p. ${String(l.page)}`;
  return null;
}

function AddressBar({ doc }: { doc: DocumentData }) {
  return (
    <div className="flex min-w-0 items-center gap-2 border-b border-divider bg-surface-2 px-2.5 py-1.5 text-md text-muted">
      <Lock size={13} aria-hidden="true" className="flex-none" />
      <span
        className="min-w-0 flex-1 truncate rounded-md border border-divider bg-surface px-2 py-0.5 font-mono text-2xs"
        title={doc.address}
      >
        {doc.address.replace(/^https?:\/\//, "")}
      </span>
      <span className="whitespace-nowrap">
        Archived snapshot{doc.captured_at ? ` · ${formatDateTime(doc.captured_at)}` : ""}
      </span>
    </div>
  );
}

function Pager({
  doc,
  itemTitle,
  page,
  pages,
  go,
}: {
  doc: DocumentData;
  itemTitle: string;
  page: number;
  pages: readonly DocumentPage[];
  go: (delta: number) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 border-b border-divider px-2.5 py-1 text-md">
      <button
        type="button"
        aria-label="Previous page"
        title="Previous page"
        disabled={page === 0}
        onClick={() => {
          go(-1);
        }}
        className="inline-flex size-7 items-center justify-center rounded-md text-muted hover:bg-row-hover disabled:opacity-40"
      >
        <ChevronLeft size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label="Next page"
        title="Next page"
        disabled={page >= pages.length - 1}
        onClick={() => {
          go(1);
        }}
        className="inline-flex size-7 items-center justify-center rounded-md text-muted hover:bg-row-hover disabled:opacity-40"
      >
        <ChevronRight size={15} aria-hidden="true" />
      </button>
      <span className="min-w-0 truncate font-strong" title={doc.section_path.join(" › ")}>
        {doc.section_path.join(" › ") || itemTitle}
      </span>
      <span className="ml-auto font-mono text-2xs whitespace-nowrap text-muted">
        {pages[page]?.key ? `${pages[page].key} · ` : ""}page {String(page + 1)} of {String(Math.max(1, pages.length))}
      </span>
    </div>
  );
}

type DocumentLink = DocumentData["links"][number];

function SurroundingLinks({ previous, next }: { previous: DocumentLink | undefined; next: DocumentLink | undefined }) {
  if (!previous && !next) return null;
  return (
    <nav aria-label="Surrounding record" className="mx-auto flex max-w-[560px] justify-between gap-3 text-md">
      {previous ? (
        <a href={previous.url} target="_blank" rel="noreferrer noopener" className="text-link">
          ← {previous.title ?? "Previous section"} <ExternalLink size={11} aria-hidden="true" className="inline" />
        </a>
      ) : (
        <span />
      )}
      {next ? (
        <a href={next.url} target="_blank" rel="noreferrer noopener" className="text-link">
          {next.title ?? "Next section"} → <ExternalLink size={11} aria-hidden="true" className="inline" />
        </a>
      ) : null}
    </nav>
  );
}

function ReplyContext({ reply }: { reply: NonNullable<DocumentData["replying_to"]> }) {
  return (
    <div className="mb-3 border-l-2 border-divider pl-2 text-md text-muted">
      <span className="flex items-baseline gap-2">
        <span className="min-w-0">Replying to {reply.label}</span>
        <a
          href={reply.url}
          target="_blank"
          rel="noreferrer noopener"
          className="ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-link"
        >
          On X
          <ExternalLink size={11} aria-hidden="true" />
        </a>
      </span>
      <p className="m-0 whitespace-pre-wrap break-words">{reply.text ?? reply.note}</p>
    </div>
  );
}

export function DocumentView({
  record,
  turns,
  selected,
  onSelect,
}: {
  record: EvidenceRecord;
  turns: readonly TurnView[];
  selected: number | null;
  onSelect: (idx: number) => void;
}) {
  const doc = record.document;
  const scroller = useRef<HTMLDivElement | null>(null);
  const pages = useMemo(() => {
    const out: DocumentPage[] = [];
    for (const b of doc?.blocks ?? []) {
      const key = pageOf(b);
      const last = out.at(-1);
      if (last?.key === key) last.blocks.push(b);
      else out.push({ key, blocks: [b] });
    }
    return out;
  }, [doc]);
  const [page, setPage] = useState(0);
  const ordinal = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of turns) {
      if (!t.locator) continue;
      map.set(t.locator.anchor, t.locator.paragraphs[0]);
    }
    return map;
  }, [turns]);

  useEffect(() => {
    if (selected === null || !scroller.current) return;
    const el = scroller.current.querySelector(`[data-block-turn="${String(selected)}"]`);
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ block: "nearest" });
      const index = pages.findIndex((p) => p.blocks.some((b) => b.turn === selected));
      if (index >= 0) setPage(index);
    }
  }, [selected, pages]);

  if (!doc) return null;
  const go = (delta: number) => {
    const next = Math.min(pages.length - 1, Math.max(0, page + delta));
    setPage(next);
    scroller.current?.querySelector(`[data-page="${String(next)}"]`)?.scrollIntoView({ block: "start" });
  };
  const previous = doc.links.find((l) => l.kind === "previous");
  const next = doc.links.find((l) => l.kind === "next");
  let paragraph = 0;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <AddressBar doc={doc} />
      <Pager doc={doc} itemTitle={record.item.title} page={page} pages={pages} go={go} />
      <div
        ref={scroller}
        className="min-h-0 flex-1 overflow-y-auto bg-surface-2 px-3 py-3 max-compact:max-h-[380px]"
        aria-label="Archived document"
      >
        {pages.map((p, pi) => (
          <div
            key={`${p.key}-${String(pi)}`}
            data-page={pi}
            className="relative mx-auto mb-3 max-w-[560px] rounded-sm border border-divider bg-bg py-4 pr-5 pl-12 text-base leading-[1.55] shadow-sm"
          >
            <div className="mb-2 flex justify-between text-2xs tracking-wider text-muted uppercase">
              <span>{longDate(record.item.dates.event?.value ?? record.item.dates.published?.value)}</span>
              <span>{p.key}</span>
            </div>
            {pi === 0 && doc.replying_to ? <ReplyContext reply={doc.replying_to} /> : null}
            {p.blocks.map((b) => {
              const own = b.turn !== null && b.turn === selected;
              const first = ordinal.get(b.anchor);
              if (!b.procedural) paragraph = first ?? paragraph + 1;
              const content = (
                <>
                  {!b.procedural ? (
                    <span className="absolute top-1 -left-9 font-mono text-2xs text-muted">¶{String(paragraph)}</span>
                  ) : null}
                  {b.speaker ? <b className="font-semibold">{b.speaker}: </b> : null}
                  <span className={b.procedural ? "text-muted italic" : undefined}>{b.text}</span>
                </>
              );
              return b.turn === null ? (
                <p key={b.anchor} className="relative mx-[-8px] mb-2 px-2 py-0.5">
                  {content}
                </p>
              ) : (
                <p key={b.anchor} className="relative mx-[-8px] mb-2">
                  <button
                    type="button"
                    data-block-turn={b.turn}
                    aria-pressed={own}
                    onClick={() => {
                      if (b.turn !== null) onSelect(b.turn);
                    }}
                    className={cx(
                      "relative block w-full rounded-sm px-2 py-0.5 text-left hover:bg-accent-soft/50",
                      own && "bg-accent-soft shadow-[inset_0_0_0_1.5px_var(--color-link)]",
                    )}
                  >
                    {content}
                  </button>
                </p>
              );
            })}
          </div>
        ))}
        <SurroundingLinks previous={previous} next={next} />
      </div>
      <div className="flex items-center gap-1.5 border-t border-divider px-2.5 py-1.5 text-2xs text-muted">
        <Lock size={12} aria-hidden="true" />
        <span>Inert copy: links and scripts are disabled. Click a paragraph to find it in the text.</span>
        {doc.artifact_href ? (
          <a href={doc.artifact_href} className="ml-auto whitespace-nowrap text-link">
            Archived file
          </a>
        ) : null}
      </div>
    </div>
  );
}

function archiveLabel(doc: DocumentData | null): string {
  if (doc?.mime_type === "application/pdf") return "PDF";
  return doc?.kind === "official_record" ? "Official record" : "Page";
}

function unitLabel(doc: DocumentData | null): string {
  if (doc?.kind === "official_record") return "contributions";
  return doc?.kind === "x_thread" ? "posts" : "paragraphs";
}

export function DocumentFacts({ record }: { record: EvidenceRecord }) {
  const k = record.key_facts;
  const doc = record.document;
  const contributions = record.turns.length;
  const rows: [string, string][] = [
    [
      "Archive",
      [archiveLabel(doc), k.clip.size_bytes === null ? null : formatBytes(k.clip.size_bytes)]
        .filter(Boolean)
        .join(" · "),
    ],
    ["Text", `Source text · ${languageName(k.transcript.language)} · ${String(contributions)} ${unitLabel(doc)}`],
    ["Translation", k.translation],
    [
      "Speakers",
      `${String(k.speakers.detected)} detected · ${String(k.speakers.named)} named · ${String(k.speakers.unresolved)} unresolved`,
    ],
    ["Checks", checksFact(k.checks)],
    ["Context", k.context],
  ];
  return (
    <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-divider px-3 py-2.5 text-md max-[1100px]:grid-cols-1">
      {rows.map(([label, value]) => (
        <div key={label} className="flex min-w-0 gap-2">
          <dt className="w-[74px] flex-none text-muted">{label}</dt>
          <dd className="m-0 min-w-0 truncate" title={value}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
